import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  IconButton,
  Modal as MuiModal,
  Stack,
  TextField,
  Typography,
  Switch,
  Paper,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Tabs,
  Tab,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useSelector } from 'react-redux';
import { removeNullValueFromObject } from '../../../../../utility/object-related';
import dayjs from 'dayjs';

const centeredMuiModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: {
    xs: 3 / 4,
    md: 1 / 2,
    lg: 1 / 3,
  },
  bgcolor: "background.paper",
  border: "2px solid #000",
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
};

const DATA_TYPES = [
  { _id: "any", name: "Any" },
  { _id: "followup_calls", name: "Follow-up calls" },
  { _id: "new_data", name: "New Data" },
  { _id: "attempted_unreached", name: "Attempted Unreached from previous months" },
  { _id: "unattempted_pending", name: "Unattempted Pending from previous months" },
  { _id: "delivered", name: "Delivered" }
];

/**
 * @typedef {Object} ExportAllocationsProps
 * @property {boolean} exportAllocationOpen - Controls the visibility of the modal
 * @property {() => void} handleExportAllocationClose - Handler for closing the modal
 * @property {boolean} exportLoading - Indicates if export operation is in progress
 * @property {(dateRange: { from: string, to: string }) => void} onExport - Handler for export operation
 * @property {(mode: string) => void} onModdedExport - Handler for export operation with mode
 * @property {{ from: string, to: string }} exportDateRange - Date range for export
 * @property {Function} setExportDateRange - Function to update date range
 * @property {boolean} enableFilter - Controls whether filtering is enabled
 * @property {Function} setEnableFilter - Function to toggle filter state
 * @property {Object} filter - Current filter configuration
 * @property {Function} setFilter - Function to update filter configuration
 */

/**
 * Modal component for exporting allocations within a specified date range
 * @param {ExportAllocationsProps} props
 */
const ExportAllocations = ({
  exportAllocationOpen = false,
  handleExportAllocationClose,
  exportLoading = false,
  onExport,
  onModdedExport,
  exportDateRange,
  setExportDateRange,
  showFilters = false,
  filter = {},
  setFilter
}) => {

  const properFilters = useMemo(() => {
    return removeNullValueFromObject(filter);
  }, [filter])

  const { users, roles, Districts, Languages } = useSelector(state => state.user || {})

  const [mode, setMode] = useState('inclusive');

  const handleExportAllocation = () => {
    if (mode === 'inclusive' || mode === 'exclusive') {
      onModdedExport(mode);
    } else {
      onExport();
    }
  };

  const handleDataTypeChange = (event) => {
    setFilter({
      ...filter,
      data_type: event.target.value
    });
  };

  const handleModeChange = (event, newMode) => {
    setMode(newMode);
  };

  const handleClose = (event, reason) => {
    const notAllowedReasons = ['escapeKeyDown', 'backdropClick'];
    if (notAllowedReasons.includes(reason)) {
      return false;
    }
    handleExportAllocationClose();
    return true;
  }

  return (
    <MuiModal
      open={exportAllocationOpen}
      onClose={handleClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box sx={centeredMuiModalStyle}>
        <IconButton
          aria-label="close"
          onClick={handleExportAllocationClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography id="modal-title" variant="h6" component="h2">
          Export Allocations
        </Typography>
        <Stack
          direction="column"
          spacing={2}
          sx={{
            p: 2,
            border: "2px solid #e0e0e0",
            borderRadius: 2,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 1,
              backgroundColor: 'grey.100',
              width: '100%'
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} width="100%">
              <Tabs
                value={mode}
                onChange={handleModeChange}
                aria-label="export mode"
                variant="fullWidth"
                sx={{
                  minHeight: '40px',
                  backgroundColor: 'white',
                  borderRadius: 1,
                  '& .MuiTab-root': {
                    minHeight: '40px',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      borderRadius: 1,
                    }
                  },
                  '& .MuiTabs-indicator': {
                    display: 'none',
                  }
                }}
              >
                <Tab label="Inclusive" value="inclusive" />
                {/* <Tab label="Exclusive" value="exclusive" />
                <Tab label="Legacy" value="legacy" /> */}
              </Tabs>
              {/* <Tooltip title="Inclusive: Shows all cases including unassigned ones. Exclusive: Shows only proper allocations without unassigned cases. Legacy: Uses the original export method before modes were created." arrow>
                <IconButton size="small">
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip> */}
            </Stack>
          </Paper>

          <Stack direction="column">
            <Typography>Select Date Range</Typography>
            <Stack direction="row" spacing={2} justifyContent="center" pt={1}>
              <TextField
                label="From"
                type="date"
                value={exportDateRange.from}
                onChange={(e) => {
                  setExportDateRange({
                    ...exportDateRange,
                    from: e.target.value,
                  });
                }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="To"
                type="date"
                value={exportDateRange.to}
                onChange={(e) => {
                  setExportDateRange({
                    ...exportDateRange,
                    to: e.target.value,
                  });
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </Stack>
          
          {/* 
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography>Enable Filters</Typography>
            <Switch
              checked={enableFilter}
              onChange={(e) => setEnableFilter(e.target.checked)}
              inputProps={{ 'aria-label': 'toggle filters' }}
            />
          </Stack> */}

          {showFilters && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: 'grey.100',
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 2
              }}
            >
              <Typography variant="subtitle1">Current Filters:</Typography>
              <Box sx={{ p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                {Object.entries(properFilters).map(([key, value]) => {
                  if (!Array.isArray(value)) {
                    // Render single value
                    return (
                      <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                        <strong>{key}:</strong> {value?.$isDayjsObject ? value.format('YYYY-MM-DD') : value}
                      </Typography>
                    );
                  } else {
                    // Render array value in an Accordion
                    return (
                      <Accordion key={key} sx={{ '&.MuiAccordion-root:before': { display: 'none' }, boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.12)', mb: 1 }}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls={`${key}-content`}
                          id={`${key}-header`}
                          sx={{ minHeight: '36px', '& .MuiAccordionSummary-content': { margin: '8px 0' } }}
                        >
                          <Typography variant="body2"><strong>{key}</strong> ({value.length})</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {value.map((item, index) => {
                            let displayValue = item;
                            if (key === 'district' && Districts) {
                              const district = Districts.find(d => d._id === item);
                              displayValue = district ? district.name : `Unknown District (${item})`;
                            } else if (key === 'allocatedToUser' && users) {
                              const user = users.find(u => u._id === item);
                              displayValue = user ? user.name : `Unknown User (${item})`;
                            } 
                            return (
                              <Typography key={`${key}-${index}`} variant="caption">
                                {displayValue}
                              </Typography>
                            );
                          })}
                        </AccordionDetails>
                      </Accordion>
                    );
                  }
                })}
                {Object.keys(properFilters).length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No filters applied
                  </Typography>
                )}
              </Box>

            </Paper>
          )}

          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              onClick={handleExportAllocation}
              variant="contained"
              disabled={exportLoading}
            >
              {exportLoading ? "Exporting..." : "Export"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </MuiModal>
  );
};

ExportAllocations.propTypes = {
  exportAllocationOpen: PropTypes.bool,
  handleExportAllocationClose: PropTypes.func.isRequired,
  exportLoading: PropTypes.bool,
  onExport: PropTypes.func.isRequired,
  onModdedExport: PropTypes.func.isRequired,
  exportDateRange: PropTypes.shape({
    from: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired
  }).isRequired,
  setExportDateRange: PropTypes.func.isRequired,
  enableFilter: PropTypes.bool,
  setEnableFilter: PropTypes.func.isRequired,
  filter: PropTypes.object,
  setFilter: PropTypes.func.isRequired
};

export default ExportAllocations;